/**
 * Take over (slice 6c §3.3, §7.2, §7.3; boards 91, 91b): the computer's live
 * desktop in control, for the person who took a handoff. No view-only layer:
 * their clicks and keys reach the computer. **I'm done** asks for an optional
 * note, then starts the agent's next turn; **Skip** starts it too. Closing
 * the sheet with neither leaves the handoff taken (its card says Continue).
 * While open, the hold is kept every 5 minutes (§5), never with a new link,
 * which would restart the stream.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@crew/ui/components/button";
import { DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Textarea } from "@crew/ui/components/textarea";
import { errorHint } from "../../app/api";
import { threadKey, threadsKey } from "../chats/api";
import { handoffDone, handoffSkip, takeHandoff, type Handoff } from "./api";
import {
  SIGN_IN_NOTE,
  useControlLink,
  useKeepHold,
  useLayoutAtOpen,
  type FirstLink,
  type Link,
} from "./desktop-link";
import { PhoneStream, SheetFrame, WideStream } from "./desktop-sheet";
import { whatToDo } from "./handoff-view";
import { DesktopPill, LockIcon, PhoneIcon } from "./marks";

/** A hold that ran out while the sheet was open (Codex on #374): no control until it's taken again. */
const LOST: Link = {
  state: "failed",
  text: "Your time on the computer ran out, so the crew could carry on. Take it again to finish.",
};

/** "YOU HAVE IT", in moss (boards 91, 91b). */
const Pill = () => (
  <DesktopPill dot="moss" moss>
    YOU HAVE IT
  </DesktopPill>
);

/**
 * I'm done's small step (§3.4): an optional note for the agent, at most 500
 * characters, then the next turn starts.
 */
function DoneStep({
  busy,
  error,
  onSend,
  onBack,
}: {
  busy: boolean;
  error: string | null;
  onSend: (note: string) => void;
  onBack: () => void;
}) {
  const [note, setNote] = useState("");
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#1B2232]/55 p-4">
      <form
        data-done-step
        className="flex w-full max-w-[440px] flex-col gap-3 rounded-panel bg-card p-5 shadow-[0_12px_32px_#0F142133]"
        onSubmit={(e) => {
          e.preventDefault();
          onSend(note.trim());
        }}
      >
        <label
          htmlFor="handoff-note"
          className="text-[17px] leading-6 font-medium tracking-[-0.01em] text-foreground"
        >
          Anything the agent should know?
        </label>
        <p className="-mt-2 text-meta text-muted-foreground">
          Optional. It reads this before it carries on.
        </p>
        <Textarea
          id="handoff-note"
          value={note}
          maxLength={500}
          rows={3}
          autoFocus
          placeholder="The delivery address was already filled in."
          onChange={(e) => setNote(e.target.value)}
          className="min-h-20 text-[15px] md:text-sm"
        />
        {error ? (
          <p role="alert" className="text-meta text-brick">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onBack} disabled={busy}>
            Back
          </Button>
          <Button type="submit" disabled={busy} className="max-md:h-11 max-md:flex-1">
            Done
          </Button>
        </div>
      </form>
    </div>
  );
}

type Parts = {
  handoff: Handoff;
  agentName: string;
  link: Link;
  again: () => void;
  onClose: () => void;
  onDone: () => void;
  onSkip: () => void;
  busy: boolean;
  error: string | null;
};

/** Board 91: the 1232 px sheet, the actions on the right, the sign-in note in the footer. */
function WideControl({ handoff, agentName, link, again, onDone, onSkip, busy, error }: Parts) {
  return (
    <>
      <div className="flex h-[60px] shrink-0 items-center gap-3.5 border-b border-border pr-5 pl-6">
        <Pill />
        <div className="flex min-w-0 flex-col">
          <DialogTitle className="truncate text-[15px] leading-[18px] font-semibold text-foreground">
            {handoff.reason}
          </DialogTitle>
          <DialogDescription className="truncate text-xs leading-4 text-muted-foreground">
            The crew's computer · {agentName} is waiting
          </DialogDescription>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" className="text-sm font-medium" onClick={onSkip} disabled={busy}>
            Skip
          </Button>
          <Button className="h-9 px-[18px] text-sm font-medium" onClick={onDone} disabled={busy}>
            I'm done
          </Button>
        </div>
      </div>
      <div className="px-6 pt-4">
        <WideStream link={link} again={again} control />
      </div>
      <div className="flex h-[52px] shrink-0 items-center gap-2.5 px-6 text-meta leading-4 text-muted-foreground">
        <LockIcon size={14} />
        <span>{SIGN_IN_NOTE}</span>
        {error ? (
          <span role="alert" className="ml-auto text-brick">
            {error}
          </span>
        ) : (
          <span className="ml-auto shrink-0 text-hint">The agent waits while you have it</span>
        )}
      </div>
    </>
  );
}

/**
 * Board 91b: full screen, zoomed on the page with Fit/Zoom, the touch hint
 * (§7.3), the sign-in note and I'm done full width at the bottom. Close sits
 * by Skip: on a phone there's no page around the sheet to tap away to.
 */
function PhoneControl({ handoff, link, again, onClose, onDone, onSkip, busy, error }: Parts) {
  return (
    <>
      <div className="flex flex-col gap-2.5 px-[18px] pt-[max(env(safe-area-inset-top),6px)] pb-4">
        <div className="flex items-center justify-between">
          <Pill />
          <div className="-mr-2 flex items-center">
            <Button
              variant="ghost"
              className="h-11 text-[15px] font-medium text-muted-foreground"
              onClick={onClose}
              disabled={busy}
            >
              Close
            </Button>
            <Button
              variant="ghost"
              className="h-11 text-[15px] font-medium"
              onClick={onSkip}
              disabled={busy}
            >
              Skip
            </Button>
          </div>
        </div>
        <DialogTitle className="text-[20px] leading-[26px] font-semibold tracking-tight text-foreground">
          {handoff.reason}
        </DialogTitle>
        <DialogDescription className="text-[15px] leading-[22px] text-copy">
          {whatToDo(handoff)}
        </DialogDescription>
      </div>
      <PhoneStream link={link} again={again} control />
      <div className="flex items-center gap-2.5 px-[18px] py-3.5 text-sm leading-5 text-muted-foreground">
        <PhoneIcon size={18} />
        Tap and type. Dragging doesn't work here; use a computer for a slider.
      </div>
      <div className="flex flex-col gap-3.5 border-t border-border px-[18px] pt-4 pb-[max(env(safe-area-inset-bottom),34px)]">
        <div className="flex items-start gap-2.5 text-meta leading-[19px] text-muted-foreground">
          <LockIcon size={14} className="mt-[3px]" />
          {SIGN_IN_NOTE}
        </div>
        {error ? (
          <p role="alert" className="text-meta text-brick">
            {error}
          </p>
        ) : null}
        <Button
          className="h-[50px] w-full rounded-[10px] text-base font-medium"
          onClick={onDone}
          disabled={busy}
        >
          I'm done
        </Button>
      </div>
    </>
  );
}

/**
 * Mounted while open (its card's `sheet`), so each opening starts at the
 * take's link, the sheet's step and no error.
 */
export function ControlSheet({
  open,
  onClose,
  handoff,
  first,
  agentName,
}: {
  open: boolean;
  /** Closes the sheet: the handoff stays taken. */
  onClose: () => void;
  handoff: Handoff;
  /** The take's answer: its control link, or why there's none. */
  first: FirstLink | null;
  agentName: string;
}) {
  // The layout it opened with, so turning a phone keeps the stream's iframe (Codex on #373).
  const wide = useLayoutAtOpen(open);
  const client = useQueryClient();
  const control = useControlLink(open, first);
  const hold = useKeepHold(open);
  // A lost hold shows why; Try again takes the handoff again (Continue), then a new link.
  const retake = useMutation({
    mutationFn: () => takeHandoff(handoff.id),
    onSuccess: (r) => {
      hold.found();
      control.againWith(r.desktop);
    },
    onError: (e) => setError(errorHint(e)),
  });
  const link = hold.lost ? LOST : control.link;
  const again = hold.lost ? () => retake.mutate() : control.again;
  const [step, setStep] = useState<"sheet" | "done">("sheet");
  const [error, setError] = useState<string | null>(null);
  const end = useMutation({
    mutationFn: (how: { kind: "done"; note: string } | { kind: "skip" }) =>
      how.kind === "done" ? handoffDone(handoff.id, how.note || null) : handoffSkip(handoff.id),
    onMutate: () => setError(null),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: threadKey(handoff.thread_id) });
      void client.invalidateQueries({ queryKey: threadsKey });
      onClose();
    },
    onError: (e) => setError(errorHint(e)),
  });
  const parts: Parts = {
    handoff,
    agentName,
    link,
    again,
    onClose,
    onDone: () => setStep("done"),
    onSkip: () => end.mutate({ kind: "skip" }),
    busy: end.isPending,
    error: step === "sheet" ? error : null,
  };
  return (
    <SheetFrame open={open} onClose={onClose} mode="control">
      {wide ? <WideControl {...parts} /> : <PhoneControl {...parts} />}
      {step === "done" ? (
        <DoneStep
          busy={end.isPending}
          error={error}
          onSend={(note) => end.mutate({ kind: "done", note })}
          onBack={() => {
            setError(null);
            setStep("sheet");
          }}
        />
      ) : null}
    </SheetFrame>
  );
}
